import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField } from 'react-final-form';
import { observer } from 'mobx-react';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Tooltip } from 'ksnm-common-ui/lib/Tooltip';
import { uniqBy } from 'lodash-es';

import { cmApi } from '@src/api';
import { controlApplicabilityEditStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityEditStore';
import { controlApplicabilityStore } from '@stores/dictionaryStores/controlApplicabilityStore/controlApplicabilityStore';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { preloaderStore } from '@stores/preloaderStore';
import type { ReportGroupsDto } from '@src/api';

import styles from './Form.module.scss';

const renderName = (value: ReportGroupsDto) => `${value.code} - ${value.name}`;

export const Form = observer(() => {
  const [stepNumsItems, setStepNumsItems] = useState<{ id: number | string; name: string }[]>([]);

  const { isEditMode, isNew } = controlApplicabilityEditStore;
  const { controlApplicability } = controlApplicabilityStore;
  const { reportGroups } = reportGroupsStore;
  const { organizations } = organizationsStore;
  const { controlVersions } = controlVersionsStore;
  const { controls } = controlsStore;

  const controlVersionsWithGroup = useMemo(
    () =>
      controlVersions.map((item) => {
        const repGroupCode = controls.find((control) => control.ctrlCode === item.ctrlCode)?.repGroupCode;

        return { ...item, repGroupCode };
      }),
    [controlVersions, controls],
  );

  useEffect(() => {
    fetchReferences(['reportGroups', 'organizations', 'controlVersions', 'controlApplicability', 'controls']);
  }, []);

  const {
    input: { value: orgCode },
  } = useField('orgCode');

  const {
    input: { value: repGroupCode },
  } = useField('repGroupCode');

  const {
    input: { value: ctrlVersCode, onChange: changeCtrlVersCode },
  } = useField('ctrlVersCode');

  const {
    input: { onChange: changeStepNums },
  } = useField('stepNums');

  const ctrlVersCodeItems = useMemo(() => {
    if (isNew) {
      const existByOrgAndGroup = controlApplicability
        .filter((control) => control.orgCode === orgCode && control.repGroupCode === repGroupCode)
        .map((control) => control.ctrlVersCode);

      return uniqBy(
        controlVersionsWithGroup.filter(
          (item) => item.repGroupCode === repGroupCode && !existByOrgAndGroup.includes(item.ctrlVersCode),
        ),
        'ctrlVersCode',
      ).map((item) => ({
        id: item.ctrlVersCode,
        name: `${item.ctrlVersCode} - ${item.ctrlVersExt} - ${item.ctrlVersDesc}`,
      }));
    }

    return controlVersionsWithGroup.map((item) => ({
      id: item.ctrlVersCode,
      name: `${item.ctrlVersCode} - ${item.ctrlVersExt} - ${item.ctrlVersDesc}`,
    }));
  }, [controlApplicability, orgCode, repGroupCode, controlVersionsWithGroup, isNew]);

  const onChange = useCallback(() => {
    changeCtrlVersCode(undefined);
    changeStepNums(undefined);
  }, [changeCtrlVersCode, changeStepNums]);

  const onChangeCtrlVersCode = useCallback(() => {
    changeStepNums(undefined);
  }, [changeStepNums]);

  useEffect(() => {
    (async () => {
      if (ctrlVersCode) {
        preloaderStore.preloader(true);
        const { data } = await cmApi.controlVersions.getControlVersionByCtrlVersCode(ctrlVersCode);
        preloaderStore.preloader(false);

        if (data) {
          const steps =
            data.steps?.map((step) => ({
              id: step.stepNum ?? '',
              name: `${step.stepNum} - ${step.stepCodeExt} - ${step.stepName}`,
            })) ?? [];
          setStepNumsItems(steps);
        }
      } else {
        setStepNumsItems([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctrlVersCode]);

  return (
    <>
      <SelectField
        items={organizations}
        name="orgCode"
        label="Организация"
        disabled={!isNew}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        required
        onChange={onChange}
      />
      <SelectField
        items={reportGroups}
        name="repGroupCode"
        label="Код группы отчетных форм"
        disabled={!isNew}
        size="small"
        groupStyle={!isEditMode}
        renderName={renderName}
        required
        onChange={onChange}
      />
      <SelectField
        items={ctrlVersCodeItems}
        name="ctrlVersCode"
        label="Версия контроля"
        disabled={!isNew}
        size="small"
        groupStyle={!isEditMode}
        required
        onChange={onChangeCtrlVersCode}
      />
      <SelectField
        items={stepNumsItems}
        name="stepNums"
        label={
          <div className={styles.label}>
            Шаги{' '}
            <Tooltip
              hideOnClick={false}
              content="Поле заполняется одним или несколькими шагами только при потребности ограничить для организации выполняемые шаги данной версии контроля (будут выполняться только выбранные шаги). В противном случае нужно оставить поле пустым (будут выполняться все шаги)."
            >
              <span>
                <Icon name="info" />
              </span>
            </Tooltip>
          </div>
        }
        disabled={!isEditMode || !ctrlVersCode}
        size="small"
        groupStyle={!isEditMode}
        mode="multiple"
      />
    </>
  );
});
